import React from 'react'
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import type { ThemePreference } from '../../api/me'
import { useMe } from '../../app/MeContext'
import { useThemePref } from '../../app/ThemeProvider'
import useUIStore from '../../store/ui'
import { usePageTitle } from '../../components/common/PageTitleContext'
import useUserSettingsStore, {
  clampChatListPreviewLines,
} from '../../store/userSettings'
import { updateUserSettings } from '../../api/userSettings'

const SettingsProfilePage: React.FC = () => {
  usePageTitle('Profile')
  const me = useMe()
  const { themePreference, setThemePreference, isSaving } = useThemePref()
  const markOnOpen = useUIStore((state) => state.markOnOpen)
  const setMarkOnOpen = useUIStore((state) => state.setMarkOnOpen)
  const identityHeadingId = React.useId()
  const generalHeadingId = React.useId()
  const inboxHeadingId = React.useId()
  const themeGroupName = React.useId()
  const previewLabelId = React.useId()

  const username = me.user?.login ?? 'Unknown'
  const fullName = me.user?.name ?? '—'
  const stationName = me.stations?.[0]?.name ?? '—'
  const chatListPreviewLines = useUserSettingsStore((state) => state.chatListPreviewLines)
  const setChatListPreviewLines = useUserSettingsStore((state) => state.setChatListPreviewLines)
  const clockFuzzy = useUserSettingsStore((state) => state.clockFuzzy)
  const setClockFuzzy = useUserSettingsStore((state) => state.setClockFuzzy)
  const [previewLines, setPreviewLines] = React.useState(chatListPreviewLines)
  const [previewSaving, setPreviewSaving] = React.useState(false)
  const [previewError, setPreviewError] = React.useState<string | null>(null)
  const [clockFuzzySaving, setClockFuzzySaving] = React.useState(false)
  const [clockFuzzyError, setClockFuzzyError] = React.useState<string | null>(null)

  React.useEffect(() => {
    setPreviewLines(chatListPreviewLines)
  }, [chatListPreviewLines])

  const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as ThemePreference
    if (value !== themePreference) {
      void setThemePreference(value)
    }
  }

  const handleMarkOnOpenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMarkOnOpen(event.target.checked)
  }

  const handleClockFuzzyChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.checked
    const previous = clockFuzzy
    setClockFuzzy(next)
    setClockFuzzySaving(true)
    setClockFuzzyError(null)

    try {
      const response = await updateUserSettings({ clock_fuzzy: next })
      setClockFuzzy(response.clock_fuzzy)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save preference.'
      setClockFuzzyError(message)
      setClockFuzzy(previous)
    } finally {
      setClockFuzzySaving(false)
    }
  }

  const handlePreviewLinesChange = (_: Event, value: number | number[]) => {
    if (Array.isArray(value)) {
      return
    }

    const next = clampChatListPreviewLines(value)
    setPreviewLines(next)
    setPreviewError(null)
  }

  const handlePreviewLinesCommit = async (_: Event | React.SyntheticEvent, value: number | number[]) => {
    if (Array.isArray(value)) {
      return
    }

    const next = clampChatListPreviewLines(value)
    setPreviewLines(next)
    setPreviewSaving(true)
    setPreviewError(null)

    try {
      const response = await updateUserSettings({ chat_list_preview_lines: next })
      setChatListPreviewLines(response.chat_list_preview_lines)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save preference.'
      setPreviewError(message)
      setPreviewLines(chatListPreviewLines)
    } finally {
      setPreviewSaving(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          Profile
        </Typography>
        <Typography color="text.secondary">
          Review your identity details and adjust personal preferences for the app and inbox.
        </Typography>
      </Stack>

      <Paper
        variant="outlined"
        component="section"
        aria-labelledby={identityHeadingId}
        sx={{ borderRadius: 2 }}
      >
        <Stack spacing={2} sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" id={identityHeadingId}>
            Identity
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Username
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {username}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Full name
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fullName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Station
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {stationName}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        component="section"
        aria-labelledby={generalHeadingId}
        sx={{ borderRadius: 2 }}
      >
        <Stack spacing={3} sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" id={generalHeadingId}>
            General settings
          </Typography>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend" id={`${generalHeadingId}-theme`} sx={{ fontWeight: 600 }}>
              Theme
            </FormLabel>
            <RadioGroup
              aria-labelledby={`${generalHeadingId}-theme`}
              name={themeGroupName}
              value={themePreference}
              onChange={handleThemeChange}
            >
              <FormControlLabel value="light" control={<Radio />} label="Light" />
              <FormControlLabel value="dark" control={<Radio />} label="Dark" />
              <FormControlLabel value="system" control={<Radio />} label="Auto" />
            </RadioGroup>
            <FormHelperText>
              {isSaving ? 'Saving preference…' : 'Choose how the interface adapts to light and dark modes.'}
            </FormHelperText>
          </FormControl>
          <FormControl component="fieldset" variant="standard">
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={clockFuzzy}
                    onChange={handleClockFuzzyChange}
                    disabled={clockFuzzySaving}
                    inputProps={{ 'aria-label': 'Fuzzy clock' }}
                  />
                }
                label="Fuzzy clock"
              />
            </FormGroup>
            <FormHelperText>
              Show rounded, human phrasing (‘almost twenty past’, ‘a quarter to’) instead of exact minutes.
            </FormHelperText>
            {clockFuzzyError ? <FormHelperText error>{clockFuzzyError}</FormHelperText> : null}
          </FormControl>
        </Stack>
        <Divider aria-hidden sx={{ mx: 3 }} />
        <Stack spacing={3} sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" id={inboxHeadingId}>
            Inbox settings
          </Typography>
          <FormControl component="fieldset" variant="standard">
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={markOnOpen} onChange={handleMarkOnOpenChange} />}
                label="Mark as read on open"
              />
            </FormGroup>
            <FormHelperText>
              Automatically mark conversations as read when you open them.
            </FormHelperText>
          </FormControl>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend" id={previewLabelId} sx={{ fontWeight: 600 }}>
              Chat list preview lines
            </FormLabel>
            <Slider
              aria-labelledby={previewLabelId}
              value={previewLines}
              min={0}
              max={5}
              step={1}
              marks={[
                { value: 0, label: '0' },
                { value: 1, label: '1' },
                { value: 2, label: '2' },
                { value: 3, label: '3' },
                { value: 4, label: '4' },
                { value: 5, label: '5' },
              ]}
              valueLabelDisplay="auto"
              onChange={handlePreviewLinesChange}
              onChangeCommitted={handlePreviewLinesCommit}
              disabled={previewSaving}
            />
            <FormHelperText>
              0 hides previews. Higher numbers show more lines in the Inbox list.
            </FormHelperText>
            {previewError ? (
              <FormHelperText error>{previewError}</FormHelperText>
            ) : null}
          </FormControl>
        </Stack>
      </Paper>
    </Stack>
  )
}

export default SettingsProfilePage
